import { TextFieldProps, Tooltip } from "@mui/material";
import TextFieldMui from "@mui/material/TextField";
import { TFormatInputField } from "../../mask-components";
import { getFieldMaskAndFormat } from "../../mask-components";
import React, { memo, useEffect, useMemo, useState } from "react";
import { useField } from "formik";
import { usePropagateRef } from "../../../hooks/usePropagateRef";
import styled from "@emotion/styled";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useYupContext } from "../../../context/RequiredFields.context";
import { debounce } from "lodash";

export type T_BASE_INPUT_PROPS = Omit<
  TextFieldProps<"outlined">,
  "variant"
>;
export interface InputFieldProps extends T_BASE_INPUT_PROPS {
  name: string;
  noUseFormik?: boolean;
  onChange?: (e: any, name?: string) => void;
  isRequired?: boolean;
  customError?: string | any;
  maskType?: TFormatInputField;
  msgTooltip?: string;
  tooltipError?: boolean;
  initialTouched?: boolean;
  onSearch?: (e: any, name?: string) => void;
}
const InputField = memo((props: InputFieldProps) => {
  const {
    name,
    msgTooltip,
    isRequired,
    initialTouched = false,
    onChange,
    ...rest
  } = props;
  const [field, meta, helpers] = useField(name);
  const [fieldValue, setFieldValue] = useState<string | number>(
    field.value
  );
  const { requiredFields } = useYupContext();
  const error = (!!meta.error && meta.touched) || props.customError;
  const className = `${props.className} ${
    requiredFields[props.name!] || props.isRequired ? "required" : ""
  }`;

  usePropagateRef({ setFieldValue, name: name, value: field.value });

  const helperText = useMemo(() => {
    if (error && meta.error && meta.error?.length > 1) {
      return meta.error;
    }
    if (error && props.customError) {
      return props.customError;
    }
  }, [error, fieldValue]);

  useEffect(() => {
    if (field.value !== fieldValue) {
      setFieldValue(field.value);
    }
  }, [field.value]);

  const onSearch = debounce((e: any) => {
    if (props.onSearch) {
      props.onSearch(e, name);
    }
  }, 500);

  const handleChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue(evt.target.value);
    onSearch(evt.target.value);
    const val: any = evt.target.value || "";

    if (onChange) {
      onChange(val, name);
    }
  };

  const handleBlur = (evt: React.FocusEvent<HTMLInputElement>) => {
    const val: any = fieldValue || "";

    props.onChange && props.onChange(val, name);
    window.setTimeout(() => {
      field.onChange({
        target: {
          name: name,
          value: props.type === "number" ? parseInt(val, 10) : val
        }
      });
      helpers.setTouched(true);
    }, 0);
  };

  const getFieldMask = getFieldMaskAndFormat(
    props?.maskType ?? "input"
  ) as unknown as any;

  const noUseFormik: T_BASE_INPUT_PROPS = {
    fullWidth: true,
    size: "small",
    className: `${props.isRequired ? "required" : ""}`,
    autoComplete: "none",
    id: `${name}-input-field`
  };

  const CONFIG_FIELD: T_BASE_INPUT_PROPS = {
    ...rest,
    ...field,
    name: name,
    id: `${name}-input-field`,
    value: fieldValue,
    onChange: handleChange,
    onBlur: handleBlur,
    fullWidth: true,
    size: "small",
    autoComplete: "none",
    className: className,
    error,
    helperText: (
      <Tooltip
        title={props.tooltipError ? helperText : ""}
        children={helperText}
      />
    ),
    InputProps: {
      ...props.InputProps,
      inputComponent: getFieldMask
    }
  };

  useEffect(() => {
    if (initialTouched) {
      helpers.setTouched(true, true);
    }
  }, [initialTouched]);

  if (props.noUseFormik)
    return (
      <Container>
        <Input {...noUseFormik} {...props} />
      </Container>
    );

  return (
    <Container>
      <Input {...CONFIG_FIELD} />
      {msgTooltip && (
        <Tooltip title={msgTooltip} children={<CustomIconInfo />} />
      )}
    </Container>
  );
});

export default InputField;

const InputWithoutFormik = (props: InputFieldProps) => {
  const baseConfig: T_BASE_INPUT_PROPS = {
    fullWidth: true,
    size: "small",
    className: `${props.isRequired ? "required" : ""}`
  };
  return <Input {...props} {...baseConfig} />;
};

export { InputWithoutFormik };

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
`;

const Input = styled(TextFieldMui)`
  .MuiFormLabel-root {
    margin-top: -1.5px;
    font-size: var(--fs-text-small);
    color: var(--m-color-blue-03);
    &.Mui-focused {
      color: var(--color-blue-main);
    }
  }

  .input-with-icon {
    .MuiFormLabel-root {
      font-size: var(--fs-text-small);
      color: var(--m-color-blue-03);
      margin-left: 8px;
      &.Mui-focused {
        color: var(--color-blue-main);
      }
    }
  }

  input.MuiInputBase-input.MuiOutlinedInput-input {
    height: var(--height-input);
  }

  & .MuiFormHelperText-root {
    color: var(--m-color-red-01) !important;
    opacity: 0.8;
    font-size: 11px;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .MuiOutlinedInput-root {
    border-radius: 20px;
    font-size: var(--fs-text-small);
  }
`;

const CustomIconInfo = styled(ErrorOutlineIcon)`
  font-size: 0.8rem;
  color: var(--m-color-blue-03);
`;
